import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, ViewChild} from '@angular/core';
import {FullCalendarComponent} from '@fullcalendar/angular';
import {CalendarOptions, DateSelectArg, DatesSetArg, EventDropArg, EventInput} from '@fullcalendar/core';
import interactionPlugin from '@fullcalendar/interaction';
import {EventResizeDoneArg} from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {take} from 'rxjs/operators';
import {FeatureService} from '../../services/feature.service';
import {DeviceSlotReservationService} from '../../services/device/device-slot-reservation.service';
import {UserService} from '../../services/user.service';
import {AbsoluteDeviceSlotReservation, DeviceSlotReservation} from '../../types/devices/device-slot-reservation';
import {DeviceSlot} from '../../types/devices/device-slot';
import {ProjectId, UserId} from '../../types/ids';
import {WmNotificationService} from '../notification/wm-notification.service';
import {ModalToolContainerService, WebmateToolConfig} from '../../tools/modaltoolcontainer/modaltoolcontainer.service';
import {TOOL_CONTAINER_DATA} from '../../tools/modaltoolcontainer/modaltoolcontainer.component';

interface SelectionRange {
    start: Date;
    end: Date;
}

export class SlotReservationEditorToolConfig implements WebmateToolConfig {
    toolName = 'Reserve Device Slot';

    constructor(public slot: DeviceSlot, public projectId: ProjectId) {
    }
}

@UntilDestroy()
@Component({
    selector: 'wm-slot-reservation-editor',
    templateUrl: './slot-reservation-editor.component.html',
    styleUrls: ['./slot-reservation-editor.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SlotReservationEditorComponent implements OnInit {
    private static readonly SNAP_INTERVAL_IN_MINUTES = 5;
    private static readonly DRAFT_EVENT_ID = 'new-slot-reservation-draft';

    slot: DeviceSlot;
    projectId: ProjectId;
    @ViewChild('reservationCalendar') reservationCalendar?: FullCalendarComponent;

    busy = true;
    calendarTitle = '';
    calendarEvents: EventInput[] = [];
    selectedRange?: SelectionRange;
    startValue?: Date;
    endValue?: Date;
    currentDate = new Date();
    validationMessage = '';
    maxSessionLength?: string;
    minSessionLength?: string;
    maxSessionLengthInSeconds = 2628000;
    minSessionLengthInSeconds = 0;
    private reservations: DeviceSlotReservation[] = [];
    private userId?: UserId;
    private syncingSelection = false;

    calendarOptions: CalendarOptions = {
        plugins: [timeGridPlugin, interactionPlugin],
        initialView: 'timeGridWeek',
        headerToolbar: false,
        height: '100%',
        selectable: true,
        selectMirror: true,
        unselectAuto: false,
        allDaySlot: false,
        nowIndicator: true,
        slotDuration: '00:30:00',
        snapDuration: '00:05:00',
        slotLabelInterval: '01:00:00',
        eventStartEditable: false,
        eventDurationEditable: false,
        eventResizableFromStart: true,
        eventOverlap: false,
        selectOverlap: false,
        expandRows: true,
        select: arg => this.selectRange(arg),
        unselect: () => this.clearSelection(),
        datesSet: arg => this.updateCalendarTitle(arg),
        eventDrop: arg => this.updateRangeFromEvent(arg),
        eventResize: arg => this.updateRangeFromEvent(arg)
    };

    constructor(@Inject(TOOL_CONTAINER_DATA) data: SlotReservationEditorToolConfig,
                private reservationService: DeviceSlotReservationService,
                private userService: UserService,
                private featureService: FeatureService,
                private notificationService: WmNotificationService,
                private toolService: ModalToolContainerService,
                private cdRef: ChangeDetectorRef) {
        this.slot = data.slot;
        this.projectId = data.projectId;
    }

    ngOnInit(): void {
        this.reservationService.getReservationsForSlot(this.slot.id, this.projectId).pipe(
            untilDestroyed(this)
        ).subscribe(reservations => {
            this.reservations = reservations;
            this.updateCalendarEvents();
            this.busy = false;
            this.revalidateSelection();
            this.cdRef.markForCheck();
        });

        this.userService.getCurrentUserData().pipe(take(1), untilDestroyed(this)).subscribe(user => {
            this.userId = user.id;
        });

        this.featureService.getFeature('webmate.device-slot-reservations').pipe(
            take(1),
            untilDestroyed(this)
        ).subscribe(feature => {
            this.maxSessionLength = feature?.['maxSessionLength'];
            this.minSessionLength = feature?.['minSessionLength'];
            this.maxSessionLengthInSeconds = this.transformToSeconds(this.maxSessionLength);
            this.minSessionLengthInSeconds = this.transformToSeconds(this.minSessionLength);
            this.revalidateSelection();
            this.cdRef.markForCheck();
        });
    }

    get canCreateReservation(): boolean {
        return !this.busy && this.userId !== undefined && this.selectedRange !== undefined && !this.validationMessage;
    }

    previousWeek(): void {
        this.reservationCalendar?.getApi().prev();
    }

    currentWeek(): void {
        this.reservationCalendar?.getApi().today();
    }

    nextWeek(): void {
        this.reservationCalendar?.getApi().next();
    }

    updateStart(value?: Date): void {
        this.startValue = value ? this.snapDate(value, 'floor') : undefined;
        this.updateRangeFromInputs();
    }

    updateEnd(value?: Date): void {
        this.endValue = value ? this.snapDate(value, 'ceil') : undefined;
        this.updateRangeFromInputs();
    }

    createReservation(): void {
        if (!this.canCreateReservation || !this.selectedRange || !this.userId) {
            return;
        }

        this.busy = true;
        this.reservationService.addNewAbsoluteReservationToSlot(
            this.slot.id,
            this.userId,
            this.projectId,
            this.selectedRange.start.toISOString(),
            this.selectedRange.end.toISOString()
        ).pipe(take(1), untilDestroyed(this)).subscribe({
            next: () => {
                this.busy = false;
                this.selectedRange = undefined;
                this.startValue = undefined;
                this.endValue = undefined;
                this.updateCalendarEvents();
                this.validationMessage = '';
                this.notificationService.toastNotification(`${this.slot.name} has been reserved.`);
                this.toolService.closeLastTool();
                this.cdRef.markForCheck();
            },
            error: error => {
                this.busy = false;
                const message = error?.status === 400 && error?.error?.message
                    ? error.error.message
                    : 'An unknown error occurred. Please try again.';
                this.notificationService.toastWarningNotification(message, 'Could not create Reservation');
                this.cdRef.markForCheck();
            }
        });
    }

    private selectRange(arg: DateSelectArg): void {
        this.selectedRange = {
            start: this.snapDate(arg.start, 'floor'),
            end: this.snapDate(arg.end, 'ceil')
        };
        this.startValue = new Date(this.selectedRange.start);
        this.endValue = new Date(this.selectedRange.end);
        this.revalidateSelection();
        this.updateCalendarEvents();
        this.syncingSelection = true;
        this.reservationCalendar?.getApi().unselect();
        this.syncingSelection = false;
        this.cdRef.markForCheck();
    }

    private clearSelection(): void {
        if (this.syncingSelection) {
            return;
        }
        this.selectedRange = undefined;
        this.startValue = undefined;
        this.endValue = undefined;
        this.revalidateSelection();
        this.cdRef.markForCheck();
    }

    private updateRangeFromInputs(): void {
        this.selectedRange = this.startValue && this.endValue
            ? {start: new Date(this.startValue), end: new Date(this.endValue)}
            : undefined;
        this.revalidateSelection();
        this.syncCalendarToRange();
        this.cdRef.markForCheck();
    }

    private syncCalendarToRange(): void {
        const calendarApi = this.reservationCalendar?.getApi();
        if (this.selectedRange) {
            calendarApi?.gotoDate(this.selectedRange.start);
        }
        this.updateCalendarEvents();
    }

    private updateRangeFromEvent(arg: EventDropArg | EventResizeDoneArg): void {
        if (arg.event.id !== SlotReservationEditorComponent.DRAFT_EVENT_ID || !arg.event.start || !arg.event.end) {
            arg.revert();
            return;
        }

        this.selectedRange = {
            start: this.snapDate(arg.event.start, 'floor'),
            end: this.snapDate(arg.event.end, 'ceil')
        };
        this.startValue = new Date(this.selectedRange.start);
        this.endValue = new Date(this.selectedRange.end);
        this.revalidateSelection();
        this.updateCalendarEvents();
        this.cdRef.markForCheck();
    }

    private updateCalendarEvents(): void {
        const events: EventInput[] = this.reservations
            .filter((reservation): reservation is AbsoluteDeviceSlotReservation => reservation instanceof AbsoluteDeviceSlotReservation)
            .map(reservation => ({
                id: reservation.id,
                start: reservation.begin,
                end: reservation.end,
                title: 'Reserved',
                editable: false,
                backgroundColor: '#d2ebff',
                borderColor: '#146eb4',
                textColor: '#1f2937'
            }));

        if (this.selectedRange) {
            events.push({
                id: SlotReservationEditorComponent.DRAFT_EVENT_ID,
                start: this.selectedRange.start,
                end: this.selectedRange.end,
                title: 'New reservation',
                editable: true,
                startEditable: true,
                durationEditable: true,
                backgroundColor: '#146eb4',
                borderColor: '#146eb4',
                textColor: '#ffffff'
            });
        }

        this.calendarEvents = events;
    }

    private updateCalendarTitle(arg: DatesSetArg): void {
        setTimeout(() => {
            this.calendarTitle = arg.view.title;
            this.cdRef.markForCheck();
        });
    }

    private revalidateSelection(): void {
        this.validationMessage = this.getValidationMessage(this.selectedRange);
    }

    private getValidationMessage(range?: SelectionRange): string {
        if ((this.startValue && !this.endValue) || (!this.startValue && this.endValue)) {
            return 'Select both a start date and an end date.';
        }
        if (!range) {
            return '';
        }
        if (range.end <= range.start) {
            return 'Select a valid time range.';
        }
        if (range.start < new Date()) {
            return 'Reservations cannot start in the past.';
        }

        const durationInSeconds = Math.round((range.end.valueOf() - range.start.valueOf()) / 1000);
        if (durationInSeconds < this.minSessionLengthInSeconds) {
            return `Reservations must last at least ${this.minSessionLength}.`;
        }
        if (this.maxSessionLengthInSeconds > 0 && durationInSeconds > this.maxSessionLengthInSeconds) {
            return `Reservations may last at most ${this.maxSessionLength}.`;
        }
        if (this.overlapsExistingReservation(range)) {
            return 'The selected time range overlaps with an existing reservation.';
        }
        return '';
    }

    private overlapsExistingReservation(range: SelectionRange): boolean {
        return this.reservations.some(reservation => {
            if (!(reservation instanceof AbsoluteDeviceSlotReservation)) {
                return false;
            }
            return range.start < new Date(reservation.end) && range.end > new Date(reservation.begin);
        });
    }

    private snapDate(input: Date, mode: 'floor' | 'ceil'): Date {
        const snapped = new Date(input.valueOf());
        snapped.setSeconds(0, 0);
        const remainder = snapped.getMinutes() % SlotReservationEditorComponent.SNAP_INTERVAL_IN_MINUTES;
        if (remainder !== 0) {
            snapped.setMinutes(snapped.getMinutes() + (mode === 'floor'
                ? -remainder
                : SlotReservationEditorComponent.SNAP_INTERVAL_IN_MINUTES - remainder));
        }
        return snapped;
    }

    private transformToSeconds(duration?: string): number {
        if (!duration) {
            return 0;
        }

        const days = duration.match(/(\d+)\s*d/);
        const hours = duration.match(/(\d+)\s*h/);
        const minutes = duration.match(/(\d+)\s*m/);
        const seconds = duration.match(/(\d+)\s*s/);
        return (days ? parseInt(days[1]) * 86400 : 0)
            + (hours ? parseInt(hours[1]) * 3600 : 0)
            + (minutes ? parseInt(minutes[1]) * 60 : 0)
            + (seconds ? parseInt(seconds[1]) : 0);
    }
}
